import { create } from 'zustand';

import { selectedCompanyStorage } from '../../../storage/company/selected-company.util';
import { SpaceSelection } from '../types/data-room.types';
import {
  ancestorsOf,
  expandedPathOf,
  nodeIdOf,
  selectedNodeId,
  SidebarExpanded,
} from '../utils/sidebar-tree.util';

type SidebarTreeStoreState = {
  expanded: SidebarExpanded;
  // Steps the selection up to the nearest surviving ancestor when the folder it sits in, or under, is gone.
  leaveDeletedFolder: (spaceId: string, folderId: string) => void;
  // A renamed folder can be the open one or one of its ancestors, reached from a search result.
  renameCrumb: (folderId: string, name: string) => void;
  // Home with the whole tree folded, as a first visit finds it.
  reset: () => void;
  resetSelection: () => void;
  // Opens a space or folder and expands the sidebar down to it.
  reveal: (selection: SpaceSelection) => void;
  // A click on a row's name: the open, expanded row folds up and stays open; any other is revealed.
  revealOrCollapse: (selection: SpaceSelection) => void;
  selection?: SpaceSelection;
  setOpen: (id: string, open: boolean, ancestors: string[]) => void;
};

const INITIAL_STATE: Pick<SidebarTreeStoreState, 'expanded' | 'selection'> = {
  expanded: {},
  selection: undefined,
};

export const SidebarTreeStore = create<SidebarTreeStoreState>()((set, get) => ({
  ...INITIAL_STATE,

  leaveDeletedFolder: (spaceId, folderId) => {
    const { expanded, selection } = get();
    // The folder and everything that was open under it are gone from the tree, so none of it may
    // go on counting towards how wide the sidebar has to be.
    const stillOpen = Object.fromEntries(
      Object.entries(expanded).filter(
        ([id, ancestors]) => id !== folderId && !ancestors.includes(folderId),
      ),
    );

    if (!selection || selection.space.id !== spaceId) {
      set({ expanded: stillOpen });

      return;
    }

    const foundIndex = selection.breadcrumbs.findIndex(({ id }) => id === folderId);

    if (foundIndex === -1 && selection.folderId !== folderId) {
      set({ expanded: stillOpen });

      return;
    }

    const crumbIndex = Math.max(foundIndex, 0);

    const breadcrumbs = selection.breadcrumbs.slice(0, crumbIndex);

    set({
      expanded: stillOpen,
      selection: { ...selection, breadcrumbs, folderId: breadcrumbs.at(-1)?.id ?? null },
    });
  },

  renameCrumb: (folderId, name) => {
    const { selection } = get();

    if (!selection?.breadcrumbs.some(({ id }) => id === folderId)) {
      return;
    }

    set({
      selection: {
        ...selection,
        breadcrumbs: selection.breadcrumbs.map((crumb) =>
          crumb.id === folderId ? { ...crumb, name } : crumb,
        ),
      },
    });
  },

  reset: () => {
    set(INITIAL_STATE);
  },

  resetSelection: () => {
    if (!get().selection) {
      return;
    }

    set({ selection: undefined });
  },

  reveal: (selection) => {
    set({ expanded: { ...get().expanded, ...expandedPathOf(selection) }, selection });
  },

  revealOrCollapse: (selection) => {
    const { expanded, reveal, selection: current, setOpen } = get();
    const id = nodeIdOf(selection);

    if (selectedNodeId(current) === id && id in expanded) {
      setOpen(id, false, ancestorsOf(selection));

      return;
    }

    reveal(selection);
  },

  setOpen: (id, open, ancestors) => {
    const { expanded } = get();
    const isOpen = id in expanded;

    if (open === isOpen) {
      return;
    }

    if (open) {
      set({ expanded: { ...expanded, [id]: ancestors } });

      return;
    }

    set({ expanded: Object.fromEntries(Object.entries(expanded).filter(([key]) => key !== id)) });
  },
}));

// Switching company does not reload the page; the kept tree would request the old company's ids.
selectedCompanyStorage.subscribe((state, previous) => {
  if (state.companyId !== previous.companyId) {
    SidebarTreeStore.getState().reset();
  }
});
